import { Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import { Col, Icon, Input, Radio, Row, Select, Spin, notification } from "antd";
import {
  createNewFinancialNature,
  updateFinancialNature,
} from "../../../services/financialNatureService";
import ButtonStandard from "../../utils/button";
import InfoTooltip from "../../utils/infoTooltip";
import { Container, Footer } from "./styles";

const validationSchema = Yup.object({
  name: Yup.string().trim().required(),
  stock: Yup.boolean().required(),
  type: Yup.string().required(),
  canBudget: Yup.boolean().required(),
});

const INITIAL_FORM_STATE = {
  id: null,
  name: null,
  stock: false,
  type: null,
  canBudget: false,
  externalIntegrationCode: null,
  farmId: null,
  groupId: null,
};

const DrawerFinancialNature = ({
  isDrawerFinancialNatureVisible = false,
  data,
  handleCloseDrawer,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const formRef = useRef(null);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  async function handleSaveNature(values) {
    setIsLoading(true);
    try {
      const body = {
        name: values.name,
        stock: values.stock,
        type: values.type,
        canBudget: values.canBudget,
        externalIntegrationCode: values.externalIntegrationCode,
        farmId: values.farmId || null,
      };

      if (values.id === null) {
        await createNewFinancialNature({
          groupId,
          farmId: values.farmId || farmId,
          body,
        });
        notification.success({
          message: translation.financialNature.messages.createSuccess,
        });
      } else {
        await updateFinancialNature({
          groupId,
          farmId: values.farmId || farmId,
          id: values.id,
          body,
        });
        notification.success({
          message: translation.financialNature.messages.updateSuccess,
        });
      }
      notification.warning({
        message: translation.financialNature.messages.adjustResultCenterWarning,
        duration: 6,
      });
      handleCloseDrawer();
    } catch (error) {
      notification.error({
        message: translation.financialNature.messages.error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  function handleDrawerVisible(visible) {
    if (visible) {
      formRef.current.resetForm(
        data
          ? data
          : { ...INITIAL_FORM_STATE, groupId: groupId, farmId: farmId }
      );
    } else {
      formRef.current.resetForm({
        ...INITIAL_FORM_STATE,
        groupId: groupId,
        farmId: farmId,
      });
    }
  }

  return (
    <Container
      title={
        data?.id == null
          ? translation.financialNature.form.titleCreateNew
          : translation.financialNature.form.titleEdit
      }
      width={700}
      onClose={handleCloseDrawer}
      maskClosable={false}
      visible={isDrawerFinancialNatureVisible}
      afterVisibleChange={handleDrawerVisible}
    >
      <Spin spinning={isLoading}>
        <Formik
          ref={formRef}
          enableReinitialize={true}
          onSubmit={handleSaveNature}
          validationSchema={validationSchema}
          render={({
            values,
            errors,
            submitCount,
            setFieldValue,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="drawerForm">
                {Object.entries(errors).length > 0 && submitCount > 0 && (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">
                      {translation.error.formError}
                    </label>
                  </Row>
                )}

                {values.id === null && (
                  <>
                    {/* Scope Selection - Farm or Group */}
                    <Row type="flex" className="rowLabel">
                      <label>{translation.financialNature.form.scope}*</label>
                    </Row>
                    <Row type="flex">
                      <Radio.Group
                        value={values.farmId !== null ? "farm" : "group"}
                        onChange={(e) => {
                          if (e.target.value === "farm") {
                            setFieldValue("farmId", farmId);
                            setFieldValue("groupId", groupId);
                          } else {
                            setFieldValue("farmId", null);
                            setFieldValue("groupId", groupId);
                          }
                        }}
                      >
                        <Radio style={{ color: "#4f4f4f" }} value="farm">
                          {translation.financialNature.form.scopeFarm}
                        </Radio>
                        <Radio style={{ color: "#4f4f4f" }} value="group">
                          {translation.financialNature.form.scopeGroup}
                        </Radio>
                      </Radio.Group>
                    </Row>
                  </>
                )}

                {/* Name */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={errors.name && submitCount > 0 ? "error" : ""}
                  >
                    {translation.financialNature.form.name}*
                  </label>
                </Row>
                <Row type="flex">
                  <Input
                    name="name"
                    value={values.name || ""}
                    maxLength={50}
                    placeholder={translation.defaultPlaceholder}
                    autoCapitalize="off"
                    onChange={(e) => {
                      setFieldValue("name", e.target.value);
                    }}
                  />
                </Row>

                {/* Type */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={errors.type && submitCount > 0 ? "error" : ""}
                  >
                    {translation.financialNature.form.type}*
                  </label>
                </Row>
                <Row type="flex">
                  <Select
                    name="type"
                    value={values.type || undefined}
                    placeholder={translation.defaultSelectPlaceholder}
                    onChange={(value) => {
                      setFieldValue("type", value);
                    }}
                  >
                    <Select.Option value="income">
                      {translation.financialNature.form.typeIncome}
                    </Select.Option>
                    <Select.Option value="expense">
                      {translation.financialNature.form.typeExpense}
                    </Select.Option>
                  </Select>
                </Row>

                {/* Stock */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={errors.stock && submitCount > 0 ? "error" : ""}
                  >
                    {translation.financialNature.form.stock}*{" "}
                    <InfoTooltip
                      title={translation.financialNature.form.stockTooltip}
                    />
                  </label>
                </Row>
                <Row type="flex">
                  <Radio.Group
                    value={values.stock}
                    onChange={(e) => {
                      setFieldValue("stock", e.target.value);
                    }}
                  >
                    <Radio style={{ color: "#4f4f4f" }} value={true}>
                      {translation.buttons.yes}
                    </Radio>
                    <Radio style={{ color: "#4f4f4f" }} value={false}>
                      {translation.buttons.no}
                    </Radio>
                  </Radio.Group>
                </Row>

                {/* CanBudget */}
                <Row type="flex" className="rowLabel">
                  <label
                    className={
                      errors.canBudget && submitCount > 0 ? "error" : ""
                    }
                  >
                    {translation.financialNature.form.canBudget}*{" "}
                    <InfoTooltip
                      title={translation.financialNature.form.canBudgetTooltip}
                    />
                  </label>
                </Row>
                <Row type="flex">
                  <Radio.Group
                    value={values.canBudget}
                    onChange={(e) => {
                      setFieldValue("canBudget", e.target.value);
                    }}
                  >
                    <Radio style={{ color: "#4f4f4f" }} value={true}>
                      {translation.buttons.yes}
                    </Radio>
                    <Radio style={{ color: "#4f4f4f" }} value={false}>
                      {translation.buttons.no}
                    </Radio>
                  </Radio.Group>
                </Row>

                {/* External Code */}
                <Row type="flex" className="rowLabel" style={{ marginTop: 16 }}>
                  <label
                    className={
                      errors.externalIntegrationCode && submitCount > 0
                        ? "error"
                        : ""
                    }
                  >
                    {translation.financialNature.form.externalIntegrationCode}
                  </label>
                </Row>
                <Row type="flex">
                  <Input
                    name="externalIntegrationCode"
                    value={values.externalIntegrationCode || ""}
                    maxLength={20}
                    placeholder={translation.defaultPlaceholder}
                    onChange={(e) =>
                      setFieldValue("externalIntegrationCode", e.target.value)
                    }
                  />
                </Row>
              </div>
              <Footer>
                <Row type="flex">
                  <Col span={24} className="buttonsDiv">
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      onClick={handleCloseDrawer}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>

                    <ButtonStandard type="submit" buttonType="type6">
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </Footer>
            </form>
          )}
        />
      </Spin>
    </Container>
  );
};

export default DrawerFinancialNature;
